import React, { useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowClockwise, Buildings, DeviceMobile, EnvelopeSimple, NotePencil, Phone, WarningCircle, type Icon,
} from '@phosphor-icons/react';
import { Chip, ImmoButton, SegmentedControl, SlidePanel, WidgetGrid } from '../../../../components/immobilien/ui';
import {
  istMieterPanel, MIETER_PANEL_KOPF, MIETER_WIDGETS, type MieterPanelKey,
} from '../../../../components/immobilien/mieterWidgets';
import { heuteIso, initialen } from '../../../../lib/immobilien/format';
import { deDatum, mieterName } from '../../../../lib/immobilien/objektDetail';
import { objektName } from '../../../../lib/immobilien/objektKopf';
import { mieteAktuell } from '../../../../lib/immobilien/einheitDetail';
import { istTicketOffen } from '../../../../lib/immobilien/tickets';
import { useMietkonto } from '../../../../lib/mietkonto/useMietkonto';
import { mieterKonto } from '../../../../lib/mietkonto/typen';
import { anredeKurz, istGruppenFilter, MIETER_GRUPPEN, personenImHaushalt, zusammenfassung, type GruppenFilter } from '../../../../lib/mieter/detail';
import { istGekuendigt, mieterStatus, PORTAL_ANZEIGE, portalZustand, STATUS_ANZEIGE, statusLabel } from '../../../../lib/mieter/liste';
import { istEhemalig } from '../../../../lib/mieter/stammdaten';
import { useMieterDetail } from '../../../../lib/mieter/useMieterDetail';
import { MieterSeitenKontext, type MieterSeitenWert } from './MieterSeitenKontext';
import { MieterWidget } from './MieterWidget';
import { MieterPanelInhalt } from './panels/MieterPanelInhalt';

/** Schnellaktion im Kopf (Prototyp: 76×64, Radius 18, --fill2). */
function Schnellaktion({ icon: IconKomp, label, href, onClick, deaktiviert }: { icon: Icon; label: string; href?: string; onClick?: () => void; deaktiviert?: string }) {
  const stil: React.CSSProperties = {
    // auf dem Handy passen alle vier in eine Reihe
    width: 'clamp(62px, 18vw, 76px)', height: 64, borderRadius: 18, gap: 4, fontSize: 11, fontWeight: 700, color: 'var(--ink)', background: 'var(--fill2)',
    border: 'none', textDecoration: 'none', opacity: deaktiviert ? 0.45 : 1,
  };
  const inhalt = <><IconKomp weight="fill" size={20} color="var(--acc)" />{label}</>;
  if (href && !deaktiviert) {
    return <a href={href} className="immo-hover-flaeche flex flex-col items-center justify-center" style={stil}>{inhalt}</a>;
  }
  return (
    <button type="button" onClick={deaktiviert ? undefined : onClick} aria-disabled={deaktiviert ? true : undefined} title={deaktiviert}
      className={`flex flex-col items-center justify-center ${deaktiviert ? 'cursor-not-allowed' : 'immo-hover-flaeche cursor-pointer'}`} style={stil}>
      {inhalt}
    </button>
  );
}

/**
 * Mieter-Detail (Handoff Mieter §5.3, Screens 10–14, 43, 44): Kopf-Karte, Schnellaktionen,
 * Gruppenfilter und 14 Widgets; ein Klick öffnet ?panel=<key>. Wechsel des Mieters baut die Seite neu auf.
 */
export default function MieterSeite({ mieterId, onNavigateToObjekt }: { mieterId: string; onNavigateToObjekt?: (id: string) => void }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { zustand, neuLaden } = useMieterDetail(mieterId);
  const daten = zustand.status === 'ok' ? zustand.daten : null;
  const objektId = daten?.mieter.objekt_id ?? null;
  const { zustand: kontoZustand } = useMietkonto(objektId);
  const heute = heuteIso();

  const rohPanel = params.get('panel');
  const panel: MieterPanelKey | null = istMieterPanel(rohPanel) ? rohPanel : null;
  const rohGruppe = params.get('grp');
  const gruppe: GruppenFilter = istGruppenFilter(rohGruppe) ? rohGruppe : 'alle';

  const oeffnePanel = useCallback((key: MieterPanelKey) => {
    setParams(p => { const n = new URLSearchParams(p); n.set('panel', key); return n; });
    window.scrollTo({ top: 0 });
  }, [setParams]);
  const verlassen = useRef<(() => void) | null>(null);
  const registriereVerlassen = useCallback((f: (() => void) | null) => { verlassen.current = f; }, []);
  const schliessePanel = useCallback(() => {
    verlassen.current = null;
    setParams(p => { const n = new URLSearchParams(p); n.delete('panel'); return n; });
  }, [setParams]);
  // Escape / „Alle Widgets“: offene Formulare fragen erst nach
  const zurueck = useCallback(() => { if (verlassen.current) verlassen.current(); else schliessePanel(); }, [schliessePanel]);
  const setzeGruppe = (g: GruppenFilter) => setParams(p => { const n = new URLSearchParams(p); if (g === 'alle') n.delete('grp'); else n.set('grp', g); return n; });

  const wert: MieterSeitenWert | null = useMemo(() => {
    if (!daten || zustand.status !== 'ok') return null;
    const m = daten.mieter;
    const mietkonto: MieterSeitenWert['mietkonto'] = !objektId ? 'ohne'
      : kontoZustand.status === 'ok' ? 'ok' : kontoZustand.status;
    const konto = kontoZustand.status === 'ok' ? kontoZustand.konto.map[mieterKonto(m.id)] ?? null : null;
    const status = mieterStatus(m, konto ? konto.saldo : null, heute);
    const email = (m.email ?? '').trim();
    const beginn = m.mietbeginn ? m.mietbeginn.slice(0, 10) : null;
    const ende = m.mietende ? m.mietende.slice(0, 10) : null;
    const name = mieterName(m);
    const offen = daten.tickets.filter(t => istTicketOffen(t.status));
    const portal = email ? portalZustand(daten.portalStatus) : 'kein';
    const objekt = daten.objekt ? objektName(daten.objekt) : '';
    const einheit = (daten.einheit?.bezeichnung ?? '').trim() || (m.einheit ?? '').trim();
    return {
      daten,
      fehler: zustand.fehler,
      name,
      vorname: (m.vorname ?? '').trim() || name,
      objektName: objekt,
      einheit,
      status,
      portal,
      email,
      beginn,
      ende,
      heute,
      miete: mieteAktuell([m], heute),
      mietkonto,
      konto,
      personen: personenImHaushalt(m.anzahl_bewohner, daten.mitmieter),
      zusammenfassung: zusammenfassung({
        vorname: (m.vorname ?? '').trim(), name, objekt, einheit, beginn, zukunft: status === 'einzug', heute,
        saldo: konto ? konto.saldo : null, offeneTickets: offen.length, letztesTicket: offen[0]?.titel ?? null,
        portalAktiv: portal === 'aktiv', gekuendigtZum: istGekuendigt(m.status) ? ende : null,
      }),
      oeffnePanel,
      neuLaden,
      registriereVerlassen,
      schliessePanel,
    };
  }, [daten, zustand, objektId, kontoZustand, heute, oeffnePanel, neuLaden, registriereVerlassen, schliessePanel]);

  if (zustand.status === 'laedt') {
    return (
      <div aria-busy="true" aria-label="Mieter wird geladen" className="flex flex-col" style={{ gap: 16 }}>
        <div className="animate-pulse" style={{ height: 128, borderRadius: 30, background: 'var(--fill2)' }} />
        <div className="animate-pulse" style={{ height: 40, width: 480, maxWidth: '100%', borderRadius: 16, background: 'var(--fill2)' }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gridAutoRows: 150, gap: 14 }}>
          {Array.from({ length: 8 }, (_, i) => <div key={i} className="animate-pulse" style={{ borderRadius: 26, background: 'var(--fill2)' }} />)}
        </div>
      </div>
    );
  }
  if (zustand.status === 'fehlt' || zustand.status === 'fehler' || !wert) {
    return (
      <div role="alert" className="immo-glas flex flex-col items-center text-center" style={{ borderRadius: 28, padding: '48px 20px', gap: 12 }}>
        <WarningCircle weight="fill" size={36} color="var(--bad)" />
        <span style={{ fontSize: 18, fontWeight: 800 }}>{zustand.status === 'fehlt' ? 'Mieter nicht gefunden' : 'Mieter konnte nicht geladen werden'}</span>
        {zustand.status === 'fehler' && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>{zustand.meldung}</span>}
        <div className="flex" style={{ gap: 8 }}>
          <ImmoButton onClick={() => navigate('/admin/mieter')}>Alle Mieter</ImmoButton>
          {zustand.status === 'fehler' && <ImmoButton art="akzent" icon={ArrowClockwise} onClick={neuLaden}>Erneut versuchen</ImmoButton>}
        </div>
      </div>
    );
  }

  const m = wert.daten.mieter;
  const st = STATUS_ANZEIGE[wert.status];
  const portal = PORTAL_ANZEIGE[wert.portal];
  const anrede = anredeKurz(m.anrede);
  const telefon = (m.mobil ?? '').trim() || (m.telefon ?? '').trim();
  const fehlerBereiche = Object.keys(wert.fehler);
  const kopf = panel ? MIETER_PANEL_KOPF[panel] : null;
  const zumObjekt = () => {
    if (!m.objekt_id) return;
    if (onNavigateToObjekt) onNavigateToObjekt(m.objekt_id); else navigate(`/admin/objekte/${encodeURIComponent(m.objekt_id)}`);
  };
  const zeitText = istEhemalig(m.status) ? (wert.ende ? `Ausgezogen am ${deDatum(wert.ende)}` : 'Ehemaliger Mieter')
    : wert.status === 'einzug' ? `Einzug am ${deDatum(wert.beginn)}` : wert.beginn ? `Mieter seit ${deDatum(wert.beginn)}` : 'Mieter';
  const kicker = [anrede, zeitText].filter(Boolean).join(' · ');
  const widgets = MIETER_WIDGETS.filter(d => gruppe === 'alle' || d.gruppe === gruppe);

  return (
    <MieterSeitenKontext.Provider value={wert}>
      <div className="flex flex-col" style={{ gap: 16 }}>
        <section className="immo-glas immo-side-in relative flex flex-wrap items-center overflow-hidden" style={{ borderRadius: 30, padding: 22, gap: 18 }} aria-label="Mieter">
          <span className="immo-akzent flex items-center justify-center shrink-0" style={{ width: 72, height: 72, borderRadius: '50%', fontSize: 24, fontWeight: 800 }}>
            {initialen(wert.name)}
          </span>
          <div className="flex flex-col min-w-0" style={{ flex: '1 1 260px', gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink3)' }}>{kicker}</span>
            <h1 className="m-0" style={{ fontSize: 34, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.05, overflowWrap: 'anywhere' }}>{wert.name}</h1>
            <div className="flex flex-wrap items-center" style={{ gap: 6 }}>
              {m.objekt_id ? (
                <button type="button" onClick={zumObjekt} title="Zum Objekt" className="immo-hover-flaeche flex items-center cursor-pointer"
                  style={{ height: 26, padding: '0 10px', borderRadius: 13, gap: 5, border: 'none', fontSize: 12, fontWeight: 700, background: 'var(--fill2)', color: 'var(--ink)' }}>
                  <Buildings weight="fill" size={13} color="var(--acc)" />{[wert.objektName, wert.einheit].filter(Boolean).join(' · ')}
                </button>
              ) : <Chip ton="neutral" groesse="s">Keine Einheit zugeordnet</Chip>}
              <Chip ton={st.ton} groesse="s" punkt>{statusLabel(wert.status, istEhemalig(m.status))}</Chip>
              <Chip ton={portal.ton} groesse="s" icon={DeviceMobile}>{portal.label}</Chip>
            </div>
          </div>
          <div className="flex flex-wrap" style={{ gap: 8 }}>
            <Schnellaktion icon={Phone} label="Anrufen" href={telefon ? `tel:${telefon.replace(/[^\d+]/g, '')}` : undefined} deaktiviert={telefon ? undefined : 'Keine Telefonnummer hinterlegt'} />
            <Schnellaktion icon={EnvelopeSimple} label="E-Mail" href={wert.email ? `mailto:${wert.email}` : undefined} deaktiviert={wert.email ? undefined : 'Keine E-Mail hinterlegt'} />
            <Schnellaktion icon={DeviceMobile} label="Portal" onClick={() => oeffnePanel('portal')} />
            <Schnellaktion icon={NotePencil} label="Notiz" onClick={() => oeffnePanel('notizen')} />
          </div>
        </section>

        {fehlerBereiche.length > 0 && (
          <div role="status" className="flex items-center flex-wrap" style={{ gap: 10, padding: '10px 14px', borderRadius: 16, background: 'var(--warn-soft)', color: 'var(--warn)', fontSize: 13, fontWeight: 600 }}>
            <WarningCircle weight="fill" size={18} />
            Einige Bereiche konnten nicht geladen werden ({fehlerBereiche.join(', ')}).
            <button type="button" onClick={neuLaden} className="cursor-pointer" style={{ border: 'none', background: 'transparent', color: 'inherit', fontWeight: 800, textDecoration: 'underline' }}>Erneut laden</button>
          </div>
        )}

        {kopf && panel ? (
          <SlidePanel key={panel} titel={kopf.titel} icon={kopf.icon} onZurueck={zurueck}>
            <MieterPanelInhalt panel={panel} />
          </SlidePanel>
        ) : (
          <>
            <SegmentedControl art="akzent" ariaLabel="Widget-Gruppen" wert={gruppe} onChange={setzeGruppe} className="self-start"
              segmente={MIETER_GRUPPEN.map(g => ({ key: g.key, label: g.label }))} />
            <WidgetGrid minSpalte={160} zeile={150} abstand={14}
              elemente={widgets.map(d => ({ key: d.key, groesse: d.groesse, inhalt: <MieterWidget def={d} s={wert} onOeffnen={() => oeffnePanel(d.key)} /> }))} />
          </>
        )}
      </div>
    </MieterSeitenKontext.Provider>
  );
}
